import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@web/env/environment';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { CoreStore } from '../store/core.store';

@Injectable({ providedIn: 'root' })
export class AdelaideService {
  private readonly document = inject(DOCUMENT);
  private readonly coreStore = inject(CoreStore);
  private readonly sessionExists$ = toObservable(this.coreStore.sessionExists);
  private readonly loaded$ = new BehaviorSubject<boolean>(false);
  private readonly ELEMENT_ID = 'adelaide-widget';

  constructor() {
    combineLatest({
      sessionExists: this.sessionExists$,
      loaded: this.loaded$,
    })
      .pipe(
        filter(({ loaded }) => loaded),
        tap(({ sessionExists }) => {
          if (sessionExists) {
            this.#loadElement();
          } else {
            this.#removeElement();
          }
        })
      )
      .subscribe();
  }

  load() {
    if (environment.adelaide) {
      const script = this.document.createElement('script');
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
      script.async = true;
      script.type = 'text/javascript';
      script.onload = () => {
        this.loaded$.next(true);
      };
      this.document.body.appendChild(script);
    }
  }

  #loadElement() {
    if (this.document.getElementById(this.ELEMENT_ID)) {
      return;
    }
    if (environment.adelaide?.agentId) {
      const element = this.document.createElement('elevenlabs-convai') as HTMLElement & {
        'agent-id': string;
        dismissible: string;
      };
      element['agent-id'] = environment.adelaide.agentId;
      element['dismissible'] = 'false';

      const div = this.document.createElement('div');
      div.id = this.ELEMENT_ID;
      div.className = 'invisible';
      this.document.body.appendChild(div);
      div.appendChild(element);
    }
  }

  toggle() {
    const div = this.document.getElementById(this.ELEMENT_ID);
    const element = div?.querySelector('elevenlabs-convai');
    const container = element?.shadowRoot?.querySelector('.rounded-compact-sheet');
    const button = container?.querySelector('button');
    if (div && div.classList.contains('invisible')) {
      div.classList.toggle('invisible');
      button?.click();
      container?.addEventListener(
        'click',
        () => {
          div.classList.toggle('invisible');
        },
        { once: true }
      );
    } else {
      button?.click();
    }
  }

  #removeElement() {
    const element = this.document.getElementById(this.ELEMENT_ID);
    if (element) {
      element.remove();
    }
  }
}
